/**
 * AdminSettingsDashboard Component
 *
 * Admin page for Q.AI System Settings Management
 * Task: T.6 - QAI Settings Admin Page
 *
 * Route: /admin/qai-settings (via admin panel)
 *
 * Features:
 * - View all settings grouped by category
 * - Update setting values with validation
 * - Reset settings to defaults
 * - Real-time feedback on changes
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Button,
  TextField,
  Slider,
  Tooltip,
  IconButton,
  Chip,
  Collapse,
  Alert
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestoreIcon from '@mui/icons-material/Restore';
import SaveIcon from '@mui/icons-material/Save';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import TokenIcon from '@mui/icons-material/Token';
import DataObjectIcon from '@mui/icons-material/DataObject';
import DescriptionIcon from '@mui/icons-material/Description';
import { toast } from 'react-toastify';

// API
import {
  getAllSettings,
  updateSetting,
  resetSetting,
  SETTINGS_CATEGORIES
} from '../../api/settingsApi';

// Category icons mapping
const CATEGORY_ICONS = {
  claude: SmartToyIcon,
  tokens: TokenIcon,
  context: DataObjectIcon,
  prompts: DescriptionIcon
};

/**
 * Single Setting Row Component
 */
const SettingRow = ({ setting, onUpdate, onReset, isUpdating }) => {
  const [localValue, setLocalValue] = useState(setting.settingValue);
  const [isDirty, setIsDirty] = useState(false);

  // Sync local value when setting changes
  useEffect(() => {
    setLocalValue(setting.settingValue);
    setIsDirty(false);
  }, [setting.settingValue]);

  const handleChange = (newValue) => {
    setLocalValue(String(newValue));
    setIsDirty(String(newValue) !== setting.settingValue);
  };

  const handleSave = async () => {
    await onUpdate(setting.settingKey, localValue);
    setIsDirty(false);
  };

  const handleReset = async () => {
    await onReset(setting.settingKey);
    setLocalValue(setting.defaultValue);
    setIsDirty(false);
  };

  const isNumeric = setting.settingType === 'int';
  const hasRange = setting.minValue !== null && setting.maxValue !== null;
  const numValue = isNumeric ? parseInt(localValue, 10) : localValue;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 2,
        px: 2,
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        '&:last-child': { borderBottom: 'none' },
        backgroundColor: isDirty ? 'rgba(124, 58, 237, 0.05)' : 'transparent',
        transition: 'background-color 0.2s'
      }}
    >
      {/* Label & Description */}
      <Box sx={{ flex: 1, mr: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>
            {setting.settingKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </Typography>
          {isDirty && (
            <Chip
              label="Modified"
              size="small"
              sx={{
                backgroundColor: 'rgba(124, 58, 237, 0.3)',
                color: '#a78bfa',
                fontSize: '0.65rem',
                height: 18
              }}
            />
          )}
        </Box>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
          {setting.description}
        </Typography>
        {hasRange && (
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', display: 'block', mt: 0.5 }}>
            Range: {setting.minValue} - {setting.maxValue} | Default: {setting.defaultValue}
          </Typography>
        )}
      </Box>

      {/* Input Control */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 300 }}>
        {isNumeric && hasRange ? (
          <Box sx={{ width: 200, mr: 2 }}>
            <Slider
              value={numValue}
              onChange={(e, val) => handleChange(val)}
              min={setting.minValue}
              max={setting.maxValue}
              valueLabelDisplay="auto"
              sx={{
                color: '#7c3aed',
                '& .MuiSlider-thumb': {
                  backgroundColor: '#7c3aed',
                },
                '& .MuiSlider-track': {
                  backgroundColor: '#7c3aed',
                },
                '& .MuiSlider-rail': {
                  backgroundColor: 'rgba(255,255,255,0.2)',
                }
              }}
            />
          </Box>
        ) : null}

        <TextField
          value={localValue}
          onChange={(e) => handleChange(e.target.value)}
          size="small"
          type={isNumeric ? 'number' : 'text'}
          inputProps={{
            min: setting.minValue,
            max: setting.maxValue
          }}
          sx={{
            width: isNumeric && hasRange ? 100 : 200,
            '& .MuiInputBase-root': {
              color: 'white',
              backgroundColor: 'rgba(255,255,255,0.05)'
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: isDirty ? '#7c3aed' : 'rgba(255,255,255,0.2)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255,255,255,0.3)',
            }
          }}
        />

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {isDirty && (
            <Tooltip title="Save changes">
              <IconButton
                size="small"
                onClick={handleSave}
                disabled={isUpdating}
                sx={{ color: '#22c55e' }}
              >
                {isUpdating ? <CircularProgress size={18} /> : <SaveIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Reset to default">
            <IconButton
              size="small"
              onClick={handleReset}
              disabled={isUpdating || localValue === setting.defaultValue}
              sx={{ color: 'rgba(255,255,255,0.5)' }}
            >
              <RestoreIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Category Section Component
 */
const CategorySection = ({ category, settings, onUpdate, onReset, isUpdating }) => {
  const [expanded, setExpanded] = useState(true);
  const categoryInfo = SETTINGS_CATEGORIES[category] || { label: category, description: '' };
  const IconComponent = CATEGORY_ICONS[category] || SettingsIcon;

  return (
    <Paper
      sx={{
        backgroundColor: '#292b2f',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 2,
        mb: 2,
        overflow: 'hidden'
      }}
    >
      {/* Category Header */}
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          cursor: 'pointer',
          backgroundColor: 'rgba(255,255,255,0.02)',
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconComponent sx={{ color: '#7c3aed', fontSize: 24 }} />
          <Box>
            <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 600 }}>
              {categoryInfo.label}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
              {categoryInfo.description}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={`${settings.length} settings`}
            size="small"
            sx={{
              backgroundColor: 'rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.7)',
              fontSize: '0.75rem'
            }}
          />
          {expanded ? <ExpandLessIcon sx={{ color: 'rgba(255,255,255,0.5)' }} /> : <ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.5)' }} />}
        </Box>
      </Box>

      {/* Settings List */}
      <Collapse in={expanded}>
        <Box>
          {settings.map((setting) => (
            <SettingRow
              key={setting.settingKey}
              setting={setting}
              onUpdate={onUpdate}
              onReset={onReset}
              isUpdating={isUpdating}
            />
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
};

/**
 * AdminSettingsDashboard Component
 */
const AdminSettingsDashboard = () => {
  const [settings, setSettings] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [error, setError] = useState(null);

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getAllSettings();

      if (result.success) {
        setSettings(result.categories || {});
        setLastRefresh(new Date());
      } else {
        setError(result.error);
        toast.error(result.error || 'Failed to load settings');
      }
    } catch (err) {
      console.error('[AdminSettingsDashboard] Error:', err);
      setError('Failed to connect to server');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Handle update
  const handleUpdate = async (key, value) => {
    setIsUpdating(true);
    try {
      const result = await updateSetting(key, value);

      if (result.success) {
        toast.success(result.message || `Updated ${key}`);
        await fetchSettings(); // Refresh to get latest values
      } else {
        toast.error(result.error || 'Update failed');
      }
    } catch (err) {
      toast.error('Failed to update setting');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle reset
  const handleReset = async (key) => {
    setIsUpdating(true);
    try {
      const result = await resetSetting(key);

      if (result.success) {
        toast.success(result.message || `Reset ${key} to default`);
        await fetchSettings();
      } else {
        toast.error(result.error || 'Reset failed');
      }
    } catch (err) {
      toast.error('Failed to reset setting');
    } finally {
      setIsUpdating(false);
    }
  };

  const categoryOrder = ['claude', 'tokens', 'context', 'prompts'];

  return (
    <Box
      sx={{
        p: 3,
        backgroundColor: '#191b20',
        minHeight: '100vh'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <SettingsIcon sx={{ color: '#7c3aed', fontSize: 28 }} />
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 600 }}>
              Q.AI System Settings
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
            Configure Claude AI parameters, token costs, and context limits.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {lastRefresh && (
            <Chip
              label={`Last updated: ${lastRefresh.toLocaleTimeString()}`}
              size="small"
              sx={{
                backgroundColor: 'rgba(255,255,255,0.05)',
                color: 'rgba(255,255,255,0.5)',
                fontSize: '0.75rem'
              }}
            />
          )}
          <Tooltip title="Refresh settings">
            <Button
              variant="outlined"
              size="small"
              onClick={fetchSettings}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
              sx={{
                borderColor: 'rgba(255,255,255,0.2)',
                color: 'rgba(255,255,255,0.7)',
                '&:hover': { borderColor: 'rgba(255,255,255,0.3)' }
              }}
            >
              Refresh
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && Object.keys(settings).length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#7c3aed' }} />
        </Box>
      ) : (
        /* Settings Categories */
        categoryOrder.map((category) => {
          const categorySettings = settings[category];
          if (!categorySettings || categorySettings.length === 0) return null;

          return (
            <CategorySection
              key={category}
              category={category}
              settings={categorySettings}
              onUpdate={handleUpdate}
              onReset={handleReset}
              isUpdating={isUpdating}
            />
          );
        })
      )}

      {/* Empty State */}
      {!isLoading && Object.keys(settings).length === 0 && !error && (
        <Paper
          sx={{
            backgroundColor: '#292b2f',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 2,
            p: 4,
            textAlign: 'center'
          }}
        >
          <SettingsIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.2)', mb: 2 }} />
          <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.5)' }}>
            No settings found
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)', mt: 1 }}>
            Settings will appear here once the backend is configured.
          </Typography>
        </Paper>
      )}

      {/* Help Section */}
      <Paper
        sx={{
          backgroundColor: 'rgba(124, 58, 237, 0.1)',
          border: '1px solid rgba(124, 58, 237, 0.2)',
          borderRadius: 2,
          p: 2,
          mt: 3
        }}
      >
        <Typography variant="subtitle2" sx={{ color: '#a78bfa', mb: 1 }}>
          Settings Overview
        </Typography>
        <Box component="ul" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem', m: 0, pl: 2.5 }}>
          <li><strong>Claude Configuration:</strong> Control AI response length and model selection</li>
          <li><strong>Token Costs:</strong> Set how many tokens each operation type consumes</li>
          <li><strong>Context Settings:</strong> Limit how much data is sent to the AI</li>
          <li><strong>Prompt Settings:</strong> Configure prompt caching behavior</li>
        </Box>
      </Paper>
    </Box>
  );
};

export default AdminSettingsDashboard;
